import { InfoDialog, Notification, PopConfirm } from "@/components"
import { Grid, MenuItem, MenuList, Select, Switch, Typography } from "@material-ui/core"
import { InboxChannel, WhatsappChannelBrokerType } from "@/pages/Inbox/protocols/ChannelProtocol"
import React, { useState } from "react"
import { InboxAdminApi, InboxWhatsappChannelApi } from "@/services/Api"
import useValidation, { ErrorType } from "@/hooks/useValidation"

type InboxChannelSettingsStatesType = {
	isStaging?: boolean
	localLabels?: string[]
}

interface InboxChannelSettingsProps {
	inboxChannel?: InboxChannel
	inboxChannelSettingsInitialData: InboxChannelSettingsStatesType
	getInboxChannel?: () => Promise<void>
	inboxChannelIntegrationId: string
}

const InboxChannelSettings: React.FC<InboxChannelSettingsProps> = (props) => {
	const { inboxChannel, getInboxChannel, children, inboxChannelSettingsInitialData, inboxChannelIntegrationId } = props

	const [inboxChannelSettingsStates, setInboxChannelSettingsStates] = useState<InboxChannelSettingsStatesType>(inboxChannelSettingsInitialData)
	const [openDialog, setOpenDialog] = useState(false)

	const { triggerValidation } = useValidation()

	const restartWhatsAppSession = async (integrationId: string) => {
		try {
			await InboxWhatsappChannelApi.get(`/whatsapp/session/${integrationId}/kill`)

			/**
			 * Add a delay to make sure the session is killed in order
			 * to start it over.
			 */
			await new Promise(resolve => setTimeout(resolve, 4000))

			await InboxWhatsappChannelApi.get(`/whatsapp/session/${integrationId}/start`)

			getInboxChannel && await getInboxChannel()
		} catch (error) {
			triggerValidation(error as ErrorType)
		}
	}

	const handleUpdateInboxChannelExtraData = async (extraData: InboxChannel["extraData"], inboxChannelId: number | undefined) => {
		try {
			if (inboxChannelId) {
				await InboxAdminApi.put(`/inbox/channel/${inboxChannelId}/update-extra-data`, {
					...extraData
				})
			}
		} catch (error) {
			getInboxChannel && getInboxChannel()
			triggerValidation(error as ErrorType)
		}
	}

	const handleAddOrRemoveInboxChannelFromStaging = async () => {
		const stagingLabel = "whatsapp-api-staging"

		const localLabels = inboxChannelSettingsStates?.localLabels ?? []
		const filteredLabels = localLabels.filter(value => value !== stagingLabel)
		const isCurrentlyStaging = Boolean(inboxChannelSettingsStates?.isStaging)

		const finalLabels = isCurrentlyStaging ? (
			filteredLabels
		) : (
			[
				...filteredLabels,
				stagingLabel
			]
		)

		setInboxChannelSettingsStates({
			...inboxChannelSettingsStates,
			isStaging: !isCurrentlyStaging,
			localLabels: finalLabels
		})

		await handleUpdateInboxChannelExtraData({
			labels: [
				...finalLabels
			]
		}, inboxChannel?.id)

		await restartWhatsAppSession(inboxChannelIntegrationId)
	}

	const changeWhatsappBroker = async (brokerType: WhatsappChannelBrokerType) => {
		try {
			await InboxWhatsappChannelApi.put("/whatsapp/broker", {
				inboxChannelId: inboxChannel?.id,
				targetBrokerType: brokerType
			})
			await getInboxChannel?.()
			Notification.success({
				message: "Biblioteca alterada com sucesso."
			})
		} catch (error) {
			triggerValidation(error as ErrorType)
			Notification.error({
				message: "Houve um erro ao tentar alterar a biblioteca."
			})
		}
	}

	const handleBrokerChange = (targetBrokerType: WhatsappChannelBrokerType) => {
		PopConfirm.open({
			description: "Alterar a biblioteca irá desconectar o canal. Por isso o cliente precisará reconectar via QR Code. Tem certeza que deseja alterar a biblioteca?",
			confirmButtonText: "CONFIRMAR",
			onConfirm: () => changeWhatsappBroker(targetBrokerType)
		})
	}

	const isWebChannel = inboxChannel?.channelType === "whatsapp"
	const isDisabledStagingOption = inboxChannel?.broker_extra_data?.broker_type && inboxChannel?.broker_extra_data?.broker_type !== "baileys"

	return <>
		{React.cloneElement(children as React.ReactElement, {
			onClick: () => setOpenDialog(true)
		})}
		<InfoDialog
			title="Configurações do Canal"
			openDialog={openDialog}
			fullWidth
			onClose={() => setOpenDialog(false)}
		>
			<MenuList>
				<MenuItem
					onClick={handleAddOrRemoveInboxChannelFromStaging}
					disableGutters
					disabled={isDisabledStagingOption}
				>
					<Grid container alignItems="center">
						<Grid xs={10}>
							Incluir Canal no Staging
						</Grid>
						<Grid xs={2}>
							<Switch
								checked={inboxChannelSettingsStates.isStaging}
								disabled={isDisabledStagingOption}
							/>
						</Grid>
					</Grid>
				</MenuItem>
				{isWebChannel && (
					<Typography style={{ padding: "5px 0px" }} variant="subtitle1">
						<Grid container alignItems="center">
							<Grid xs={9}>
								Biblioteca Utilizada Neste Canal
							</Grid>
							<Grid xs={3}>
								<Select
									style={{ height: "45px" }}
									fullWidth
									value={inboxChannel?.broker_extra_data?.broker_type ?? "baileys"}
									onChange={(event) => handleBrokerChange(event.target.value as WhatsappChannelBrokerType)}
									variant="outlined"
								>
									<MenuItem value="baileys">Baileys</MenuItem>
									<MenuItem value="zapi">Z-API</MenuItem>
								</Select>
							</Grid>
						</Grid>
					</Typography>
				)}
			</MenuList>
		</InfoDialog>
	</>
}

export default InboxChannelSettings
